import { clamp } from './curve'
import { REST_ANGLES } from './kinematics'
import { CARRY_ARMS } from './reference'
import type { PlaneKey } from './joints'
import {
  hipHeight,
  pointDistance,
  pointFor,
  sideDistance,
  type HumanoidModel,
} from './model'
import { carrying, type GaitTask, type SimState } from './state'

/**
 * Gang RECHNEN statt vorschreiben.
 *
 * Der Referenzgang aus den Normkurven bleibt unangetastet — er ist der
 * Nullzustand und jederzeit wieder einschaltbar. Hier entsteht die Bewegung
 * stattdessen aus einer Aufgabe: Schrittlänge, Standphasenanteil, Fussfreigang,
 * Aufsetz- und Abstosswinkel. Der Fusspfad wird in Weltkoordinaten gelegt, die
 * Gelenkwinkel folgen daraus.
 *
 * DIE KETTE, IN DIESER REIHENFOLGE:
 *
 *   1. Fussabrollen        → wo die Sprunggelenkmitte in der Standphase liegt
 *   2. Beckenhöhe          → umgekehrtes Pendel (Gehen) bzw. Wurfparabel (Laufen)
 *   3. Schwungbahn         → knickfreier Anschluss an beide Standphasenenden
 *   4. Inverse Kinematik   → Hüft- und Kniewinkel aus der Beinlänge
 *   5. Sprunggelenk        → was die Kette übrig lässt für die Sollneigung
 *
 * Nur die Zahlen der Aufgabe unterscheiden die Gangarten. Die Kette ist jedes
 * Mal dieselbe — das ist die eigentliche Aussage dieses Moduls.
 */

/** Erdbeschleunigung, m/s². */
export const G = 9.81

const DEG = Math.PI / 180

/** Anteile der Standphase: Fersendrehung bis 16 %, flacher Fuss bis 74 %, dann Ballen. */
const HEEL_UNTIL = 0.16
const TOE_FROM = 0.74

export const SIDES = ['R', 'L'] as const
export type Side = (typeof SIDES)[number]

/* -------------------------------------------------------------------------
   Masse in Weltkoordinaten
   ------------------------------------------------------------------------- */

/**
 * Masse der Beinkette in WELTKOORDINATEN.
 *
 * OHNE den Aufbaumassstab. Das sieht falsch aus und ist richtig — hier lag beim
 * Portieren der einzige echte Fehler, und er ist lehrreich:
 *
 * Die Vorlage führt `LEN.thigh = Punktabstand / 1000 / SCALE` und rechnet später
 * `LEN.thigh * SCALE`. Der Massstab kürzt sich vollständig weg; übrig bleibt der
 * blanke Punktabstand. Er existiert nur, weil die Three.js-Gruppe des Roboters
 * mit SCALE skaliert ist und `LEN` die Geometrie INNERHALB dieser Gruppe setzt.
 *
 * Wer die Kürzung übersieht und einmal zu viel skaliert, bekommt ein um 4,5 %
 * zu langes Bein. Das klemmt dann in der Kinematik — hier sind es 54 Anschläge
 * je Zyklus im Gehen, wo null hingehören. Sichtbar wird das erst am Ende der
 * Kette, und ohne Test gar nicht.
 *
 * Der Massstab bleibt im Modell, gehört aber zur Darstellung und nicht zur
 * Rechnung.
 */
export interface GaitLengths {
  /** Oberschenkel, Punktabstand in Metern. */
  thigh: number
  /** Schienbein, Punktabstand in Metern. */
  shank: number
  /** Sprunggelenkmitte bis Sohle. */
  foot: number
  /** Halber Beinachsabstand. */
  hipY: number
  /** Höhe der Hüftnickachse über dem Boden in der Standmitte. */
  hipH: number
}

/**
 * Die Längen einer Seite.
 *
 * DIE BAHNPLANUNG NIMMT IMMER DIE RECHTE SEITE, die inverse Kinematik die
 * eigene. Wer eine gewollte Unsymmetrie anlegt, bekommt damit genau die
 * Auskunft, die er sucht: die Beckenbahn ist dieselbe wie vorher, und das
 * kürzere Bein meldet sich als Anschlag und als Fehler am Boden. Die Bahn
 * stattdessen auf ein Mittelmass zu legen hiesse, den Gang stillschweigend
 * umzuplanen und die Unsymmetrie damit unsichtbar zu machen.
 */
export function gaitLengths(model: HumanoidModel, task: GaitTask, side: Side = 'R'): GaitLengths {
  const thigh = sideDistance(model, 'hipN', 'knee', side)
  const shank = sideDistance(model, 'knee', 'ankle', side)
  const ankle = pointFor(model, 'ankle', side)
  const sole = pointFor(model, 'sole', side)
  const foot = Math.abs(ankle.z - sole.z) / 1000
  const hipY = pointFor(model, 'hipN', side).y / 1000
  return { thigh, shank, foot, hipY, hipH: task.flex * (thigh + shank) + foot }
}

/* -------------------------------------------------------------------------
   Fussgeometrie und Abrollen
   ------------------------------------------------------------------------- */

/** Ein Punkt in der Sagittalebene. x vorn, z oben. */
interface P2 {
  x: number
  z: number
}

/**
 * Die drei Bodenpunkte im Fusssystem, Ursprung Sprunggelenkmitte.
 *
 * Der Fuss ist ein starrer Körper. In der Standphase dreht er sich nacheinander
 * um Ferse, flache Sohle und Ballen, ohne zu rutschen — daraus folgt die Lage
 * der Sprunggelenkmitte, nicht umgekehrt. Das ist die richtige Richtung: der
 * Boden gibt vor, wo der Fuss ist, und das Bein muss folgen.
 */
export interface FootGeometry {
  sole: P2
  toe: P2
  heel: P2
}

export function footGeometry(model: HumanoidModel): FootGeometry {
  const a = model.points.ankle
  const s = model.points.sole
  const m = model.points.mtp
  const sole = { x: (s.x - a.x) / 1000, z: (s.z - a.z) / 1000 }
  const toe = { x: (m.x - a.x) / 1000, z: (m.z - a.z) / 1000 }
  /* Ferse hinter dem Sohlenpunkt. In der Vorlage ein Zahlenliteral mitten in
     der Fussgeometrie — hier eine Modellgrösse, damit ein anderer Fuss nicht
     stillschweigend dieselbe Ferse behält. */
  const heel = { x: sole.x - model.heelOffset, z: sole.z }
  return { sole, toe, heel }
}

/** Drehung in der Sagittalebene, positiv = Zehen nach oben. */
const rot2 = (p: P2, a: number): P2 => {
  const c = Math.cos(a)
  const s = Math.sin(a)
  return { x: p.x * c - p.z * s, z: p.x * s + p.z * c }
}

export interface StancePose {
  x: number
  z: number
  /** Fussneigung in Grad, positiv = Zehen hoch. */
  pitch: number
}

/**
 * Sprunggelenkmitte und Fussneigung über der Standphase, bezogen auf den Boden.
 *
 * `u` läuft von 0 (Fersenaufsatz) bis 1 (Zehenablösung). Rückgabe in
 * Weltkoordinaten mit dem Fersenaufsatz bei x = 0.
 */
export function stancePose(u: number, task: GaitTask, foot: FootGeometry): StancePose {
  let pitch: number
  let pivot: P2
  let pivotWorld: P2

  if (u < HEEL_UNTIL) {
    /* Drehung um die Ferse: der Aufsetzwinkel läuft linear auf null. */
    const w = u / HEEL_UNTIL
    pitch = task.touch * (1 - w) * DEG
    pivot = foot.heel
    pivotWorld = { x: 0, z: 0 }
  } else if (u < TOE_FROM) {
    /* Flacher Fuss. */
    pitch = 0
    pivot = foot.sole
    pivotWorld = { x: -foot.heel.x + foot.sole.x, z: 0 }
  } else {
    /* Drehung um den Ballen. Weich anlaufend und weich endend (3u²−2u³),
       damit die Winkelbeschleunigung an den Rändern nicht springt. */
    const w = (u - TOE_FROM) / (1 - TOE_FROM)
    pitch = task.push * w * w * (3 - 2 * w) * DEG
    pivot = foot.toe
    pivotWorld = { x: -foot.heel.x + foot.toe.x, z: 0 }
  }

  const r = rot2(pivot, pitch)
  return { x: pivotWorld.x - r.x, z: pivotWorld.z - r.z, pitch: pitch / DEG }
}

/* -------------------------------------------------------------------------
   Beckenhöhe
   ------------------------------------------------------------------------- */

/**
 * Höhe der Hüftnickachse über der Sohle.
 *
 * GEHEN: Becken am höchsten in der Standmitte — umgekehrtes Pendel.
 *
 * LAUFEN: in der Standphase federt das Bein ein, danach fliegt der Körper frei.
 * Die Flugbahn ist eine Wurfparabel, und das ist der Punkt: Abwurfgeschwindigkeit
 * und Einfederung folgen aus der Flugzeit und sind deshalb NICHT frei wählbar.
 * Wer sie trotzdem vorgibt, bekommt einen Körper, der schneller fällt als er
 * darf — und Bodenkräfte, die niemand aufbringt.
 */
export function pelvisHeight(
  phase: number,
  lengths: GaitLengths,
  task: GaitTask,
  cycleTime: number,
): number {
  const ds = 2 * task.duty
  if (ds >= 0.999) {
    /* Dauernder Bodenkontakt: reines Auf und Ab mit doppelter Schrittfrequenz. */
    return lengths.hipH - task.bob * 0.5 * (1 + Math.cos(4 * Math.PI * phase))
  }

  const half = cycleTime / 2
  const flightTime = (1 - ds) * half
  const v0 = (G * flightTime) / 2
  const spring = (v0 * (ds * half)) / Math.PI

  const q = ((((phase % 0.5) + 0.5) % 0.5) * 2)
  if (q < ds) return lengths.hipH - spring * Math.sin((Math.PI * q) / ds)

  const t = (q - ds) * half
  return lengths.hipH + v0 * t - 0.5 * G * t * t
}

/* -------------------------------------------------------------------------
   Fusspfad über den ganzen Zyklus
   ------------------------------------------------------------------------- */

export interface AnklePose extends StancePose {
  /** 0 in der Stand-, 1 in der Schwungphase. */
  swing: 0 | 1
}

/**
 * Sprunggelenkmitte gegenüber dem Becken über den ganzen Zyklus.
 *
 * Standphase aus dem Abrollen, Schwungphase als weiche Verbindung zwischen dem
 * Ende der einen und dem Beginn der nächsten Standphase.
 *
 * WARUM KUBISCHE HERMITE UND NICHT EINFACHER: die Schwungbahn muss an beiden
 * Enden nicht nur in der LAGE, sondern auch in der GESCHWINDIGKEIT anschliessen.
 * Ein Knick dort erzeugt eine Beschleunigungsspitze im Schwerpunkt — und damit
 * eine Kraft, die kein Fuss aufbringt. Deshalb werden die Randableitungen der
 * Standphase numerisch abgegriffen und als Tangenten übergeben.
 *
 * `swingBlend` nimmt diese Tangenten zurück, wenn die Bahn sonst weiter
 * ausgreifen würde, als das Bein reicht (siehe `generateGait`).
 */
export function anklePose(
  phase: number,
  task: GaitTask,
  foot: FootGeometry,
  swingBlend: number,
): AnklePose {
  const d = task.duty
  const stride = 2 * task.step

  /* Nullpunkt so legen, dass die Standphase beckenfest symmetrisch liegt.
     Während der Standphase läuft das Becken um 2 · Schritt · Standanteil weiter —
     nur diese Strecke muss das Bein überstreichen, nicht die ganze Schrittlänge.
     Beim Gehen sind beide fast gleich, beim Laufen mit Flugphase nicht. */
  const s00 = stancePose(0, task, foot)
  const H = task.step * task.duty - s00.x

  if (phase < d) {
    const st = stancePose(phase / d, task, foot)
    return { x: H + st.x - stride * phase, z: st.z, pitch: st.pitch, swing: 0 }
  }

  const e = stancePose(1, task, foot)
  const s0 = stancePose(0, task, foot)
  const xe = H + e.x - stride * d
  const ze = e.z
  const pe = e.pitch
  const xs = H + s0.x
  const zs = s0.z
  const ps = s0.pitch

  const du = 1e-3
  const dE = stancePose(1 - du, task, foot)
  const dS = stancePose(du, task, foot)
  const mex = ((e.x - dE.x) / du / d - stride) * swingBlend
  const mez = ((e.z - dE.z) / du / d) * swingBlend
  const mep = ((e.pitch - dE.pitch) / du / d) * swingBlend
  const msx = ((dS.x - s0.x) / du / d - stride) * swingBlend
  const msz = ((dS.z - s0.z) / du / d) * swingBlend
  const msp = ((dS.pitch - s0.pitch) / du / d) * swingBlend

  const u = (phase - d) / (1 - d)
  const span = 1 - d
  const u2 = u * u
  const u3 = u2 * u
  const h00 = 2 * u3 - 3 * u2 + 1
  const h10 = u3 - 2 * u2 + u
  const h01 = -2 * u3 + 3 * u2
  const h11 = u3 - u2
  const hermite = (p0: number, m0: number, p1: number, m1: number) =>
    h00 * p0 + h10 * span * m0 + h01 * p1 + h11 * span * m1

  /* Quadrierter Sinus: Steigung an beiden Enden null, damit der Freigang den
     Anschluss nicht wieder verdirbt. */
  const bump = Math.sin(Math.PI * u)

  return {
    x: hermite(xe, mex, xs, msx),
    z: hermite(ze, mez, zs, msz) + task.clear * bump * bump,
    pitch: hermite(pe, mep, ps, msp),
    swing: 1,
  }
}

/* -------------------------------------------------------------------------
   Inverse Kinematik der Beinkette
   ------------------------------------------------------------------------- */

export interface LegAngles {
  /** Hüftbeugung in Grad. */
  hip: number
  /** Kniebeugung in Grad, positiv = gebeugt. */
  knee: number
  /** +1 wenn auf die Streckgrenze geklemmt, −1 auf die Beugegrenze, sonst 0. */
  clip: -1 | 0 | 1
  /** Verlangte Beinlänge als Anteil der gestreckten — über 1 heisst: greift zu weit. */
  reach: number
}

/**
 * Beinkette in der Sagittalebene: Hüftgelenkmitte im Ursprung, Sprunggelenkmitte
 * bei (ax, az). Gesucht Beuge- und Streckwinkel.
 *
 * Das Klemmen auf 99,5 % der gestreckten Länge ist kein Sicherheitsabstand,
 * sondern rechnerisch nötig: bei genau 100 % ist der Kniewinkel null und die
 * Ableitung nach der Beinlänge unendlich. Ein Bein, das exakt durchgestreckt
 * geführt wird, erzeugt in der Winkelgeschwindigkeit einen Sprung.
 *
 * `clip` zählt mit, wie oft geklemmt wurde — das ist die Warnung, dass die
 * Aufgabe nicht zur Maschine passt, und darf nicht verschwiegen werden.
 */
export function legIK(ax: number, az: number, thigh: number, shank: number): LegAngles {
  const d = Math.hypot(ax, az)
  let clip: -1 | 0 | 1 = 0
  let dd = d
  const dmax = (thigh + shank) * 0.995
  const dmin = Math.abs(thigh - shank) + 0.02
  if (dd > dmax) {
    dd = dmax
    clip = 1
  }
  if (dd < dmin) {
    dd = dmin
    clip = -1
  }

  const ck = (thigh * thigh + shank * shank - dd * dd) / (2 * thigh * shank)
  const knee = Math.PI - Math.acos(clamp(ck, -1, 1))
  const beta = Math.asin(clamp((shank * Math.sin(knee)) / dd, -1, 1))
  const alpha = Math.atan2(ax, -az)

  return {
    hip: ((alpha + beta) * 180) / Math.PI,
    knee: (knee * 180) / Math.PI,
    clip,
    reach: d / (thigh + shank),
  }
}

/* -------------------------------------------------------------------------
   Die Erzeugung
   ------------------------------------------------------------------------- */

export type AngleTable = Record<string, Partial<Record<PlaneKey, Record<Side, Float32Array>>>>

export interface GaitReport {
  /** Wie oft das Bein auf die Streckgrenze geklemmt wurde. */
  clipHigh: number
  /** Wie oft auf die Beugegrenze. */
  clipLow: number
  /** Grösste verlangte Beinlänge als Anteil der gestreckten. */
  reach: number
  /** Fortbewegungsgeschwindigkeit, m/s. */
  speed: number
  /** Wie weit die Standrandableitungen übernommen wurden (1 = knickfrei). */
  swingBlend: number
  /** Flugzeit je Schritt, s. Null beim Gehen. */
  flight: number
  /** Steighöhe des Schwerpunkts im Flug, m. */
  rise: number
  /** Einfederung in der Standphase, m. */
  spring: number
  /**
   * Kleinster seitlicher Abstand eines Fusses zur Mittellinie, m.
   *
   * DIE ZAHL, AN DER MAN DIE ABDUKTORENFÜHRUNG SIEHT. Mit ihr steht der Fuss auf
   * seiner Spur, und der Abstand bleibt bei der halben Spurbreite. Ohne sie hängt
   * das Bein am Becken, und der Fuss wandert mit der Beckenverlagerung zur
   * Mittellinie — bei genug Verlagerung negativ: er kreuzt sie, und der Roboter
   * tritt sich auf den eigenen Fuss.
   */
  trackClearance: number
  /** Die Aufgabe passt nicht zur Maschine. */
  warn: boolean
}

export interface GaitResult {
  angles: AngleTable
  /**
   * VORGEGEBENE Höhe der Hüftnickachse über dem Boden, je Stützstelle, in Metern.
   *
   * Vorgegeben und nicht gemessen: das ist die Bahn, aus der die Gelenkwinkel
   * folgen. Wo der Körper danach wirklich steht, entscheidet erst der
   * Bodenkontakt (`ground.ts`) — die beiden dürfen nicht verwechselt werden.
   *
   * Sie steht hier, weil die Erzeugung sie ohnehin rechnet. Wer sie später ein
   * zweites Mal aus derselben Aufgabe herleitet, hat zwei Rechnungen für
   * dieselbe Grösse, und die laufen beim ersten Eingriff auseinander.
   */
  hipTrack: Float32Array
  report: GaitReport
  lengths: GaitLengths
  foot: FootGeometry
  /** Stützstellen je Zyklus. */
  samples: number
  /** Dauer des Doppelschritts, s. */
  cycleTime: number
}

/** Dauer des Doppelschritts in Sekunden. */
export const cycleTime = (cadence: number): number => 120 / cadence

/**
 * Grösstmöglicher knickfreier Anschluss, der die Beinlänge nicht sprengt.
 *
 * Die Standphase gibt vor, wie weit das Bein ohnehin ausgreift; die Schwungbahn
 * darf nicht darüber hinausgehen, sonst klemmt die Kinematik. Statt die Bahn zu
 * beschneiden — was den Knick zurückbrächte — werden die Randtangenten
 * schrittweise zurückgenommen, bis es passt. Höchstens 24 Halbierungsschritte;
 * darunter ist die Aufgabe schlicht nicht ausführbar, und das soll man sehen.
 */
function findSwingBlend(
  task: GaitTask,
  foot: FootGeometry,
  lengths: GaitLengths,
  samples: number,
  cycle: number,
): number {
  const legs = lengths.thigh + lengths.shank

  let stanceReach = 0
  for (let i = 0; i < samples; i++) {
    const t = i / samples
    if (t >= task.duty) continue
    const a = anklePose(t, task, foot, 1)
    const h = pelvisHeight(t, lengths, task, cycle)
    stanceReach = Math.max(stanceReach, Math.hypot(a.x, h - a.z) / legs)
  }

  const limit = Math.max(0.995, stanceReach)
  let blend = 1
  for (let k = 0; k < 24; k++) {
    let worst = 0
    for (let i = 0; i < samples; i++) {
      const t = i / samples
      if (t < task.duty) continue
      const a = anklePose(t, task, foot, blend)
      const h = pelvisHeight(t, lengths, task, cycle)
      worst = Math.max(worst, Math.hypot(a.x, h - a.z) / legs)
    }
    if (worst <= limit || blend < 0.05) break
    blend *= 0.85
  }
  return blend
}

const emptyTable = (samples: number): Record<Side, Float32Array> => ({
  R: new Float32Array(samples + 1),
  L: new Float32Array(samples + 1),
})

/**
 * Erzeugt die Winkeltabellen für den gerechneten Gang.
 *
 * Rein: gleiche Eingaben, gleiche Tabellen. Kein Zugriff auf die Szene, keine
 * Zeit, kein Zustand ausserhalb der Argumente.
 */
export function generateGait(model: HumanoidModel, state: SimState): GaitResult {
  const task = state.task
  const samples = state.samples
  const cycle = cycleTime(state.trim.cadence)
  const lengths = gaitLengths(model, task)
  /* Je Seite, für die inverse Kinematik. Die Bahnplanung darüber bleibt bei
     `lengths` — der rechten Seite; siehe `gaitLengths`. */
  const sideLengths: Record<Side, GaitLengths> = {
    R: lengths,
    L: gaitLengths(model, task, 'L'),
  }
  const foot = footGeometry(model)
  const blend = findSwingBlend(task, foot, lengths, samples, cycle)

  const angles: AngleTable = {}
  for (const joint of ['hip', 'knee', 'ankle', 'pelvis', 'shoulder', 'elbow', 'lumbar', 'thorax']) {
    angles[joint] = {
      sag: emptyTable(samples),
      fro: emptyTable(samples),
      tra: emptyTable(samples),
    }
  }

  /* Die vorgegebene Beckenbahn — dieselbe für beide Seiten. `pelvisHeight` hat
     die Periode eines HALBEN Zyklus, deshalb ist sie für Rechts und Links
     identisch und darf hier einmal für den ganzen Körper stehen. */
  const hipTrack = new Float32Array(samples + 1)

  let clipHigh = 0
  let clipLow = 0
  let reach = 0
  let trackClearance = Infinity

  /*
    ABDUKTORENFÜHRUNG — der Schalter `hipAbd`.

    An: die Hüfte führt den Fuss in der Frontalebene auf SEINE SPUR. Der
    Frontalwinkel ist dann genau der, den es braucht, damit der Fuss bei
    ±`task.track` steht, während sich das Becken darüber verlagert.

    Aus: der Frontalwinkel bleibt null, das Bein hängt senkrecht unter seiner
    Hüftnickachse. Der Fuss steht dann dort, wo ihn das Becken hinträgt —
    seitlicher Abstand `hipY·cos(roll) + shift` statt `track`, und weil sich das
    Becken zum Standbein verlagert, wandert der Schwungfuss zur Mittellinie.

    Das ist keine Kosmetik: es ist der Grund, warum ein Zweibeiner überhaupt
    Abduktoren braucht, und die Wirkung steht als `trackClearance` im Bericht.
  */
  const guided = state.features.hipAbd && state.features.frontal

  /*
    WELCHE MERKMALSSCHALTER DER GERECHNETE GANG KENNT — und welche nicht.

    Er kennt: `pelFro` (die seitliche Beckenbewegung), `pelTra` (die
    Beckendrehung), `hipAbd` (die Abduktorenführung), `frontal` (überhaupt eine
    Frontal- und Transversalebene), `trunk` (die Rumpfkette) und `arms` (den
    Armschwung). Jeder davon nimmt hier einen Beitrag heraus, den er zu
    verantworten hat.

    Er kennt NICHT: `pelSag` und `mtp`. Nicht aus Nachlässigkeit — der
    gerechnete Gang hat die Anteile schlicht nicht. Die Beckenkippung steht bei
    ihm fest (`task.lean`) und schwingt nicht, und das Zehengrundgelenk bekommt
    keine Winkelvorgabe; ein starrer Fuss ist bereits der Zustand. Beide Schalter
    wirken deshalb nur auf den AUFGEZEICHNETEN Gang, wo es die Kurven gibt.

    Das steht hier, weil es sonst wie ein Fehler aussieht: wer im gerechneten
    Betrieb `pelSag` umlegt und keine Zahl wandern sieht, hält den Schalter für
    tot. Er ist nicht tot, er hat hier nichts zu tun.
  */
  const rolls = state.features.pelFro
  const turns = state.features.pelTra
  const flexible = state.features.trunk
  const swings = state.features.arms
  /*
    Die Arme halten die Kiste. Dieselbe Bedingung, die die Kiste überhaupt
    einblendet und sie als dreizehntes Massensegment führt — sie steht in
    `state.ts`, damit es genau eine gibt.

    `swings` schlägt sie NICHT: wer die Armbewegung im Modell abschaltet, will
    ein reines Beinmodell und keine Haltung, die den Schwerpunkt verschiebt.
  */
  const holding = swings && carrying(state)
  const spatial = state.features.frontal

  for (let i = 0; i <= samples; i++) {
    const t = (i % samples) / samples

    /* Frontalebene zuerst: das Becken verlagert sich ZUM Standbein hin, nicht
       von ihm weg. Genau das entlastet die Rollachse. Bei t = 0 steht rechts,
       also nach −y. Ohne `pelFro` bleibt das Becken waagerecht und steht still. */
    const shift = rolls ? -task.sway * Math.sin(2 * Math.PI * t) : 0
    const pelvisRoll = rolls ? Math.atan2(shift, 0.4) : 0

    /* Beckendrehung um die Hochachse verlängert den Schritt, verschiebt aber
       auch die Hüftgelenkmitte nach vorn — das muss die Kinematik wissen. */
    const yaw = turns ? task.yaw * Math.sin(2 * Math.PI * t) * DEG : 0

    hipTrack[i] = pelvisHeight(t, lengths, task, cycle)

    for (const side of SIDES) {
      const phase = side === 'L' ? (t + 0.5) % 1 : t
      const s = side === 'R' ? 1 : -1
      /* Die Längen DIESER Seite. Ohne Unsymmetrie sind sie die der Bahnplanung. */
      const own = sideLengths[side]
      const a = anklePose(phase, task, foot, blend)

      /* Das Rollen hebt die eine Hüftnickachse und senkt die andere. */
      const hipZ = hipTrack[i] - s * own.hipY * Math.sin(pelvisRoll)
      /* Wo die Hüftnickachse seitlich steht — der Ausgangspunkt für beide Fälle. */
      const hipY = s * own.hipY * Math.cos(pelvisRoll) + shift
      const dy = guided ? s * task.track - hipY : 0
      /* Wo der Fuss dadurch WIRKLICH steht, ins Körpermass gerechnet: positiv
         heisst auf der eigenen Seite, negativ heisst über der Mittellinie. */
      trackClearance = Math.min(trackClearance, s * (hipY + dy))

      angles.pelvis.tra![side][i] = yaw / DEG
      /*
        Die Rumpfkette hält gegen: der Brustkorb bleibt im Raum stehen, während
        sich das Becken dreht. Ohne sie (`trunk` aus) ist der Oberkörper starr am
        Becken — er dreht dann 1:1 mit, und der Drall um die Hochachse, den der
        Armschwung ausgleichen soll, wird deutlich grösser.
      */
      angles.lumbar.tra![side][i] = flexible ? -yaw / DEG : 0

      const drop = hipZ - a.z

      /* Das Bein steht schräg: massgebend ist die Länge in der BEINEBENE, nicht
         die senkrechte Höhe. Sonst steht der Fuss neben dem Boden. */
      const ax = a.x - s * own.hipY * Math.sin(yaw)
      const az = -Math.hypot(drop, dy)
      const k = legIK(ax, az, own.thigh, own.shank)

      if (k.clip > 0) clipHigh++
      if (k.clip < 0) clipLow++
      reach = Math.max(reach, k.reach)

      /* Die Beckenneigung dreht die ganze Beinkette mit — der Hüftwinkel wird um
         sie angehoben, damit der Oberschenkel im Raum dort steht, wo ihn die
         Kinematik verlangt. Sonst steht der Fuss nicht auf dem Boden. */
      angles.hip.sag![side][i] = k.hip + task.lean
      angles.pelvis.sag![side][i] = task.lean - 3 /* die Pose addiert 3° Grundneigung */
      angles.knee.sag![side][i] = k.knee

      /* Sprunggelenk: was die Kette übrig lässt, damit die Sohle die Sollneigung hat. */
      angles.ankle.sag![side][i] = a.pitch - (k.hip - k.knee)

      /* Ohne Frontal- und Transversalebene bleibt ein reines Sagittalmodell:
         Ab- und Anspreizung entfallen, das Becken bleibt waagerecht. */
      angles.hip.fro![side][i] = spatial ? ((-Math.atan2(dy, drop) * 180) / Math.PI) * s : 0
      angles.ankle.fro![side][i] = -angles.hip.fro![side][i] * 0.45
      angles.pelvis.fro![side][i] = spatial ? (pelvisRoll * 180) / Math.PI : 0

      /*
        Arme schwingen gegenphasig zum gleichseitigen Bein und dämpfen den Drall.
        Ohne `arms` stehen sie in Ruhelage — nicht auf null: ein Ellbogen mit 0°
        wäre durchgestreckt, und die Ruhelage dieser Maschine ist es nicht.
      */
      if (holding) {
        /* Beim Tragen halten die Arme die Kiste, sie schwingen nicht — siehe
           `CARRY_ARMS`. Der Rest-Schwung bleibt, damit die Figur nicht steif
           wirkt. */
        angles.shoulder.sag![side][i] =
          CARRY_ARMS.shoulder - k.hip * task.arm * CARRY_ARMS.swing
        angles.elbow.sag![side][i] = CARRY_ARMS.elbow
      } else {
        angles.shoulder.sag![side][i] = swings ? -k.hip * task.arm : 0
        angles.elbow.sag![side][i] = swings
          ? 18 + 14 * Math.max(0, -Math.cos(2 * Math.PI * phase))
          : REST_ANGLES.elbow.sag!
      }
    }
  }

  const speed = task.step * (state.trim.cadence / 60)
  const ds = 2 * task.duty
  const flight = ds < 0.999 ? ((1 - ds) * cycle) / 2 : 0
  const v0 = (G * flight) / 2
  const spring = (v0 * ((ds * cycle) / 2)) / Math.PI

  return {
    angles,
    hipTrack,
    lengths,
    foot,
    samples,
    cycleTime: cycle,
    report: {
      clipHigh,
      clipLow,
      reach,
      speed,
      swingBlend: blend,
      flight,
      rise: (v0 * v0) / (2 * G),
      spring,
      trackClearance: Number.isFinite(trackClearance) ? trackClearance : 0,
      warn: clipHigh > 0 || clipLow > 0,
    },
  }
}

/** Winkel an einer beliebigen Phase, linear zwischen den Stützstellen. */
export function sampleAngle(
  result: GaitResult,
  joint: string,
  plane: PlaneKey,
  side: Side,
  phase: number,
): number | null {
  const table = result.angles[joint]?.[plane]?.[side]
  if (!table) return null
  const x = ((((phase % 1) + 1) % 1) * result.samples)
  const i = Math.floor(x)
  const f = x - i
  return table[i] * (1 - f) + table[Math.min(result.samples, i + 1)] * f
}

/** Nur zur Gegenprobe: Hüfthöhe aus den Punkten, unskaliert. */
export const nominalHipHeight = hipHeight

/** Nur zur Gegenprobe: Abstand zweier Punkte. */
export const distance = pointDistance
